import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Package, LayoutGrid, CheckCircle2, XCircle, Tag, Image as ImageIcon } from "lucide-react";
import { useCachedState } from "@/hooks/use-cached-state";

export const Route = createFileRoute("/_authenticated/store/")({
  head: () => ({ meta: [{ title: "Dashboard Store — MI Al-Munajah Admin" }] }),
  component: StoreDashboard,
});

type Stats = {
  products: number;
  categories: number;
  active: number;
  out: number;
  promo: number;
  banners: number;
};

function StoreDashboard() {
  const [s, setS] = useCachedState<Stats | null>("store:stats", null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = (q: any) => q.then((r: any) => r.count ?? 0);
      const [products, categories, active, out, promo, banners] = await Promise.all([
        c(supabase.from("store_products").select("*", { count: "exact", head: true })),
        c(supabase.from("store_categories").select("*", { count: "exact", head: true })),
        c(supabase.from("store_products").select("*", { count: "exact", head: true }).eq("status", "active")),
        c(supabase.from("store_products").select("*", { count: "exact", head: true }).eq("status", "out_of_stock")),
        c(supabase.from("store_products").select("*", { count: "exact", head: true }).eq("is_promo", true)),
        c(supabase.from("store_banners").select("*", { count: "exact", head: true }).eq("is_active", true)),
      ]);
      if (cancelled) return;
      setS({ products, categories, active, out, promo, banners });
    })();
    return () => { cancelled = true; };
  }, []);

  const loading = s === null;
  const stats = s ?? { products: 0, categories: 0, active: 0, out: 0, promo: 0, banners: 0 };

  const cards = [
    { label: "Total Produk", value: stats.products, icon: Package, color: "bg-blue-500/10 text-blue-600", to: "/store/products" },
    { label: "Kategori", value: stats.categories, icon: LayoutGrid, color: "bg-purple-500/10 text-purple-600", to: "/store/categories" },
    { label: "Produk Aktif", value: stats.active, icon: CheckCircle2, color: "bg-green-500/10 text-green-600", to: "/store/products" },
    { label: "Produk Habis", value: stats.out, icon: XCircle, color: "bg-orange-500/10 text-orange-600", to: "/store/products" },
    { label: "Produk Promo", value: stats.promo, icon: Tag, color: "bg-pink-500/10 text-pink-600", to: "/store/products" },
    { label: "Banner Aktif", value: stats.banners, icon: ImageIcon, color: "bg-cyan-500/10 text-cyan-600", to: "/store/banners" },
  ];

  return (
    <div>
      <PageHeader title="Dashboard Store" description="Ringkasan katalog Toko Sekolah." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Link key={c.label} to={c.to}>
            <Card className="p-5 rounded-2xl hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{c.label}</div>
                  <div className="text-2xl font-bold">{loading ? "…" : c.value}</div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
